const PLAYER_CONTEXT = "player.js";
const PLAYER_VERSION = "0.0.11";
const PLAYER_READY_TIMEOUT = 10000;
const PLAYBACK_START_TIMEOUT = 6000;
const PLAYBACK_STALL_TIMEOUT = 8000;
const FRAME_REVEAL_FALLBACK_TIMEOUT = 750;
const OFFSCREEN_TEARDOWN_TIMEOUT = 2000;
const PAUSE_EVENT_GRACE_TIMEOUT = 1500;
const SAFE_IFRAME_PERMISSIONS = new Set([
  "autoplay",
  "clipboard-write",
  "encrypted-media",
  "fullscreen",
  "picture-in-picture",
  "web-share"
]);

class ResponsiveVideoEmbed extends HTMLElement {
  constructor() {
    super();
    this._generation = 0;
    this._onActivationIntersection = this._onActivationIntersection.bind(this);
    this._onBreakpointChange = this._onBreakpointChange.bind(this);
    this._onMessage = this._onMessage.bind(this);
    this._onMotionPreferenceChange = this._onMotionPreferenceChange.bind(this);
    this._onPlaybackIntersection = this._onPlaybackIntersection.bind(this);
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
  }

  connectedCallback() {
    this._generation += 1;
    this._desktopSource = this._readSource('template[data-video-embed="desktop"]');
    this._mobileSource = this._readSource('template[data-video-embed="mobile"]');
    this._applySourceAspectRatios();
    this._playerContainer = this.querySelector(".responsive-video-embed__player");
    this._mediaQuery = window.matchMedia(window.themeVariables?.mediaQueries?.sm || "(min-width: 700px)");
    this._motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
    this._activeMode = this._getMode();

    const bounds = this.getBoundingClientRect();
    this._isNearViewport = bounds.bottom >= -200 && bounds.top <= window.innerHeight + 200;
    this._isInViewport = bounds.bottom > 0 && bounds.top < window.innerHeight;
    this._manualPlayback = false;
    this._desiredPlayback = this.hasAttribute("autoplay") && !this._motionPreference.matches;
    this._resetPlayerState();

    this._mediaQuery.addEventListener("change", this._onBreakpointChange);
    this._motionPreference.addEventListener("change", this._onMotionPreferenceChange);
    document.addEventListener("visibilitychange", this._onVisibilityChange);
    window.addEventListener("message", this._onMessage);

    this._activationObserver = new IntersectionObserver(this._onActivationIntersection, {
      rootMargin: "200px 0px",
      threshold: 0
    });
    this._playbackObserver = new IntersectionObserver(this._onPlaybackIntersection, {
      threshold: 0.01
    });
    this._activationObserver.observe(this);
    this._playbackObserver.observe(this);

    if (this._isNearViewport && !document.hidden && this._shouldActivate()) {
      this._activateAfterPosterPaint();
    }
  }

  disconnectedCallback() {
    this._teardownPlayer();
    this._activationObserver?.disconnect();
    this._playbackObserver?.disconnect();
    this._mediaQuery?.removeEventListener("change", this._onBreakpointChange);
    this._motionPreference?.removeEventListener("change", this._onMotionPreferenceChange);
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    window.removeEventListener("message", this._onMessage);
  }

  _readSource(selector) {
    const template = this.querySelector(selector);
    const embedCode = template?.getAttribute("data-embed-code");
    if (!embedCode) {
      return null;
    }

    const parsedEmbed = new DOMParser().parseFromString(embedCode, "text/html");
    const iframeList = parsedEmbed.querySelectorAll("iframe");
    if (iframeList.length !== 1) {
      return null;
    }

    const sourceIframe = iframeList[0];
    const sourceUrl = sourceIframe.getAttribute("src");
    if (!sourceUrl) {
      return null;
    }

    try {
      const url = new URL(sourceUrl, window.location.href);
      if (url.protocol !== "https:") {
        return null;
      }

      const permissions = (sourceIframe.getAttribute("allow") || "")
        .split(";")
        .map((permission) => permission.trim().split(/\s+/)[0].toLowerCase())
        .filter((permission, index, list) => SAFE_IFRAME_PERMISSIONS.has(permission) && list.indexOf(permission) === index);

      return {
        aspectRatio: this._readAspectRatio(sourceIframe),
        allowFullscreen: sourceIframe.hasAttribute("allowfullscreen"),
        permissions,
        title: sourceIframe.getAttribute("title") || "Video",
        url
      };
    } catch (_error) {
      return null;
    }
  }

  _readAspectRatio(sourceIframe) {
    const wrapper = sourceIframe.parentElement;
    const wrapperAspectRatio = wrapper?.style.aspectRatio;
    if (wrapperAspectRatio) {
      return wrapperAspectRatio;
    }

    const paddingTop = wrapper?.style.paddingTop;
    if (paddingTop?.endsWith("%")) {
      const paddingPercent = Number.parseFloat(paddingTop);
      if (Number.isFinite(paddingPercent) && paddingPercent > 0) {
        return `100 / ${paddingPercent}`;
      }
    }

    const widthAttribute = sourceIframe.getAttribute("width")?.trim();
    const heightAttribute = sourceIframe.getAttribute("height")?.trim();
    const width = /^\d+(?:\.\d+)?$/.test(widthAttribute || "") ? Number(widthAttribute) : NaN;
    const height = /^\d+(?:\.\d+)?$/.test(heightAttribute || "") ? Number(heightAttribute) : NaN;
    return Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0 ? `${width} / ${height}` : null;
  }

  _applySourceAspectRatios() {
    const desktopAspectRatio = this._desktopSource?.aspectRatio;
    const mobileAspectRatio = this._mobileSource?.aspectRatio || desktopAspectRatio;

    // A server-rendered ratio reserves the poster's final box before this
    // module runs. Keep that box immutable during the poster/player handoff.
    if (desktopAspectRatio && !this.style.getPropertyValue("--video-embed-desktop-aspect-ratio")) {
      this.style.setProperty("--video-embed-desktop-aspect-ratio", desktopAspectRatio);
    }
    if (mobileAspectRatio && !this.style.getPropertyValue("--video-embed-mobile-aspect-ratio")) {
      this.style.setProperty("--video-embed-mobile-aspect-ratio", mobileAspectRatio);
    }
  }

  _getMode() {
    return !this._mediaQuery?.matches && this._mobileSource ? "mobile" : "desktop";
  }

  _getSource() {
    return this._activeMode === "mobile" ? this._mobileSource : this._desktopSource;
  }

  _sourceHasVisibleControls(source = this._getSource()) {
    if (!source) {
      return false;
    }

    const background = source.url.searchParams.get("background")?.toLowerCase();
    if (["1", "true"].includes(background)) {
      return false;
    }

    const controls = source.url.searchParams.get("controls")?.toLowerCase();
    return controls === undefined || !["0", "false"].includes(controls);
  }

  _shouldActivate() {
    return !this.hasAttribute("autoplay") || !this._motionPreference.matches || this._sourceHasVisibleControls();
  }

  _shouldPlay() {
    return this._desiredPlayback && this._isInViewport && !document.hidden && (!this._motionPreference.matches || this._manualPlayback);
  }

  async _activateAfterPosterPaint() {
    const generation = this._generation;
    const poster = this.querySelector(".responsive-video-embed__poster img");
    if (poster && !poster.complete) {
      await new Promise((resolve) => {
        poster.addEventListener("load", resolve, { once: true });
        poster.addEventListener("error", resolve, { once: true });
      });
    } else if (poster?.decode) {
      await poster.decode().catch(() => {});
    }

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (generation === this._generation && this.isConnected && this._isNearViewport && !document.hidden && this._shouldActivate()) {
      this._activate();
    }
  }

  _activate() {
    if (this._iframe) {
      return;
    }

    const source = this._getSource();
    if (!source || !this._playerContainer) {
      this._markFailed();
      return;
    }

    const url = new URL(source.url);
    this._isLivid = url.hostname === "livid.com" || url.hostname.endsWith(".livid.com");
    const isAutoplayEmbed = this.hasAttribute("autoplay");
    const controlsHidden = !this._sourceHasVisibleControls(source);

    if (this._isLivid) {
      url.searchParams.set("dnt", "true");
      url.searchParams.set("playsinline", "1");
      url.searchParams.set("initial_quality", "360p");
      url.searchParams.set("max_quality", "720p");
      url.searchParams.set("autoplay", isAutoplayEmbed && this._shouldPlay() ? "1" : "0");
      if (isAutoplayEmbed && this._shouldPlay()) {
        url.searchParams.set("preload", "auto");
      }
    }

    const iframe = document.createElement("iframe");
    iframe.src = url.href;
    iframe.setAttribute("aria-label", source.title);
    iframe.referrerPolicy = "strict-origin-when-cross-origin";
    iframe.loading = isAutoplayEmbed ? "eager" : "lazy";
    if (source.permissions.length > 0) {
      iframe.allow = source.permissions.join("; ");
    }
    if (source.allowFullscreen) {
      iframe.setAttribute("allowfullscreen", "");
    }

    this.toggleAttribute("player-controls-hidden", controlsHidden);
    if (this._isLivid && controlsHidden) {
      iframe.tabIndex = -1;
      iframe.style.pointerEvents = "none";
    }

    iframe.addEventListener("load", () => {
      if (!this._isLivid) {
        this.setAttribute("loaded", "");
        this._exposeProvider(true);
      }
    }, { once: true });

    this._iframe = iframe;
    this._playerOrigin = url.origin;
    this._playerContainer.replaceChildren(iframe);
    if (!this._isInViewport) {
      this._scheduleOffscreenTeardown();
    }

    if (this._isLivid) {
      this._readyTimeout = window.setTimeout(() => {
        if (!this._playerReady) {
          this._markFailed();
        }
      }, PLAYER_READY_TIMEOUT);
    }
  }

  _resetPlayerState() {
    this._playerReady = false;
    this._pauseRequested = false;
    this._programmaticPauseEventPending = false;
    this._resumeAfterPause = false;
    this._earlyAutoplayRetryUsed = false;
    this._lastPlaybackTime = null;
    this._lastProgressAt = 0;
    this._framePending = false;
    this._setPlaying(false);
  }

  _teardownPlayer() {
    this._generation += 1;
    this._clearTimers();
    this._iframe?.remove();
    this._iframe = null;
    this._playerOrigin = null;
    this._isLivid = false;
    this._resetPlayerState();
    this._exposeProvider(false);
    this.removeAttribute("loaded");
    this.removeAttribute("failed");
    this.removeAttribute("has-frame");
    this.removeAttribute("player-controls-hidden");
  }

  _clearTimers() {
    window.clearTimeout(this._readyTimeout);
    window.clearTimeout(this._playbackStartTimeout);
    window.clearTimeout(this._stallTimeout);
    window.clearTimeout(this._frameRevealTimeout);
    window.clearTimeout(this._offscreenTeardownTimeout);
    window.clearTimeout(this._pauseEventGraceTimeout);
  }

  _onBreakpointChange() {
    const nextMode = this._getMode();
    if (nextMode === this._activeMode) {
      return;
    }

    this._activeMode = nextMode;
    this._teardownPlayer();
    if (this._isNearViewport && !document.hidden && this._shouldActivate()) {
      this._activateAfterPosterPaint();
    }
  }

  _onActivationIntersection(entries) {
    this._isNearViewport = entries[entries.length - 1].isIntersecting;
    if (this._isNearViewport && !this._iframe && !document.hidden && this._shouldActivate()) {
      this._activateAfterPosterPaint();
    }
  }

  _onPlaybackIntersection(entries) {
    this._isInViewport = entries[entries.length - 1].isIntersecting;
    if (!this._isInViewport) {
      this._pause();
      this._scheduleOffscreenTeardown();
    } else if (!this._iframe && this._isNearViewport && !document.hidden && this._shouldActivate()) {
      window.clearTimeout(this._offscreenTeardownTimeout);
      this._activateAfterPosterPaint();
    } else if (this._shouldPlay()) {
      window.clearTimeout(this._offscreenTeardownTimeout);
      this._play();
    } else {
      window.clearTimeout(this._offscreenTeardownTimeout);
    }
  }

  _scheduleOffscreenTeardown() {
    window.clearTimeout(this._offscreenTeardownTimeout);
    this._offscreenTeardownTimeout = window.setTimeout(() => {
      if ((document.hidden || !this._isInViewport) && this._iframe) {
        this._teardownPlayer();
      }
    }, OFFSCREEN_TEARDOWN_TIMEOUT);
  }

  _onVisibilityChange() {
    if (document.hidden) {
      this._pause();
      this._scheduleOffscreenTeardown();
      return;
    }

    window.clearTimeout(this._offscreenTeardownTimeout);
    if (!this._iframe && this._isNearViewport && this._shouldActivate()) {
      this._activateAfterPosterPaint();
    } else if (this._shouldPlay()) {
      this._play();
    } else if (!this._isInViewport) {
      this._scheduleOffscreenTeardown();
    }
  }

  _onMotionPreferenceChange(event) {
    if (!this.hasAttribute("autoplay")) {
      return;
    }

    this._desiredPlayback = !event.matches;
    this._manualPlayback = false;
    this._teardownPlayer();
    if (this._isNearViewport && !document.hidden && this._shouldActivate()) {
      this._activateAfterPosterPaint();
    }
  }

  _onMessage(event) {
    if (!this._iframe || event.source !== this._iframe.contentWindow || event.origin !== this._playerOrigin) {
      return;
    }

    let message = event.data;
    if (typeof message === "string") {
      try {
        message = JSON.parse(message);
      } catch (_error) {
        return;
      }
    }
    if (!message || message.context !== PLAYER_CONTEXT) {
      return;
    }

    if (message.event === "ready") {
      window.clearTimeout(this._readyTimeout);
      this._playerReady = true;
      this.setAttribute("loaded", "");
      ["play", "pause", "ended", "error", "timeupdate"].forEach((eventName) => this._post("addEventListener", eventName, eventName));
      this._post("getPaused", undefined, "getPaused");
      this._post("getCurrentTime", undefined, "getCurrentTime");
      if (this._shouldPlay()) {
        this._play();
      } else if (this._sourceHasVisibleControls()) {
        this._exposeProvider(true);
      }
    } else if (message.event === "play") {
      this._pauseRequested = false;
      this._resumeAfterPause = false;
      if (this._programmaticPauseEventPending) {
        this._armPauseEventGrace();
      }
      this._manualPlayback = this._motionPreference.matches;
      this._desiredPlayback = true;
      this._handlePlaybackStarted();
      if (!this._shouldPlay()) {
        this._pause();
      }
    } else if (message.event === "pause" || message.event === "ended") {
      const requested = this._pauseRequested || this._programmaticPauseEventPending;
      const resume = requested && (this._resumeAfterPause || this._shouldPlay());
      this._confirmPause();
      if (this._programmaticPauseEventPending) {
        this._consumeProgrammaticPauseEvent();
      }
      const earlyAutoplayPause =
        message.event === "pause" &&
        !requested &&
        this.hasAttribute("autoplay") &&
        !this.hasAttribute("has-frame") &&
        this._isInViewport &&
        !document.hidden &&
        !this._motionPreference.matches;

      if (earlyAutoplayPause && !this._earlyAutoplayRetryUsed) {
        this._earlyAutoplayRetryUsed = true;
        this._desiredPlayback = true;
        this._play();
      } else if (earlyAutoplayPause) {
        this._desiredPlayback = false;
        this._exposeProvider(this._sourceHasVisibleControls());
      } else if (message.event === "pause" && !requested && this._sourceHasVisibleControls()) {
        this._manualPlayback = false;
        this._desiredPlayback = false;
      } else if (resume) {
        this._play();
      }
    } else if (message.event === "timeupdate" || message.event === "getCurrentTime") {
      this._recordPlaybackTime(message.value?.seconds ?? message.value);
    } else if (message.event === "getPaused") {
      const paused = message.value === true || message.value === "true";
      if (paused && this._pauseRequested) {
        const resume = this._resumeAfterPause || this._shouldPlay();
        this._confirmPause();
        if (this._shouldPlay()) {
          this._armPauseEventGrace();
        }
        if (resume) {
          this._play();
        }
      } else if (!paused) {
        this._handlePlaybackStarted();
        if (!this._shouldPlay()) {
          this._pause();
        }
      }
    } else if (message.event === "error") {
      this._markFailed();
    }
  }

  _post(method, value, listener) {
    if (!this._iframe?.contentWindow || !this._playerOrigin) {
      return;
    }
    const message = { context: PLAYER_CONTEXT, version: PLAYER_VERSION, method };
    if (value !== undefined) {
      message.value = value;
    }
    if (listener) {
      message.listener = listener;
    }
    this._iframe.contentWindow.postMessage(JSON.stringify(message), this._playerOrigin);
  }

  _play() {
    if (!this._playerReady || !this._shouldPlay()) {
      return;
    }
    if (this._pauseRequested) {
      this._resumeAfterPause = true;
    }
    if (this._programmaticPauseEventPending) {
      this._armPauseEventGrace();
    }
    this._post("play");
    window.clearTimeout(this._playbackStartTimeout);
    this._playbackStartTimeout = window.setTimeout(() => {
      if (!this.hasAttribute("has-frame") && this._shouldPlay()) {
        this.setAttribute("failed", "");
        this._setPlaying(false);
        this._exposeProvider(this._sourceHasVisibleControls());
      }
    }, PLAYBACK_START_TIMEOUT);
  }

  _pause() {
    if (!this._playerReady || !this.hasAttribute("playing")) {
      return;
    }
    this._pauseRequested = true;
    this._programmaticPauseEventPending = true;
    window.clearTimeout(this._playbackStartTimeout);
    window.clearTimeout(this._stallTimeout);
    this._post("pause");
    this._post("getPaused", undefined, "getPaused");
  }

  _confirmPause() {
    window.clearTimeout(this._stallTimeout);
    window.clearTimeout(this._playbackStartTimeout);
    this._pauseRequested = false;
    this._resumeAfterPause = false;
    this._setPlaying(false);
  }

  _armPauseEventGrace() {
    window.clearTimeout(this._pauseEventGraceTimeout);
    this._pauseEventGraceTimeout = window.setTimeout(() => {
      this._programmaticPauseEventPending = false;
    }, PAUSE_EVENT_GRACE_TIMEOUT);
  }

  _consumeProgrammaticPauseEvent() {
    window.clearTimeout(this._pauseEventGraceTimeout);
    this._programmaticPauseEventPending = false;
  }

  _handlePlaybackStarted() {
    window.clearTimeout(this._playbackStartTimeout);
    this.removeAttribute("failed");
    this._exposeProvider(false);
    this._setPlaying(true);
    this._lastProgressAt = performance.now();
    window.clearTimeout(this._frameRevealTimeout);
    this._frameRevealTimeout = window.setTimeout(() => this._markFrameAfterPaint(), FRAME_REVEAL_FALLBACK_TIMEOUT);
    this._watchForStall();
  }

  _recordPlaybackTime(value) {
    const currentTime = Number(value);
    if (!Number.isFinite(currentTime) || currentTime < 0) {
      return;
    }

    const previousTime = this._lastPlaybackTime;
    this._lastPlaybackTime = currentTime;
    const advanced = previousTime === null ? currentTime > 0.01 : currentTime > previousTime + 0.01;
    const looped = previousTime !== null && previousTime > currentTime + 0.5;
    if ((advanced || looped) && this.hasAttribute("playing")) {
      window.clearTimeout(this._frameRevealTimeout);
      this._lastProgressAt = performance.now();
      this._markFrameAfterPaint();
      this._watchForStall();
    }
  }

  _markFrameAfterPaint() {
    if (this._framePending || this.hasAttribute("has-frame")) {
      return;
    }
    this._framePending = true;
    const generation = this._generation;
    const iframe = this._iframe;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this._framePending = false;
      if (generation === this._generation && iframe === this._iframe && this.isConnected && this.hasAttribute("playing")) {
        this.removeAttribute("failed");
        this._exposeProvider(false);
        this.setAttribute("has-frame", "");
      }
    }));
  }

  _watchForStall() {
    window.clearTimeout(this._stallTimeout);
    if (!this.hasAttribute("playing") || !this._shouldPlay()) {
      return;
    }
    this._stallTimeout = window.setTimeout(() => {
      if (this.hasAttribute("playing") && this._shouldPlay() && performance.now() - this._lastProgressAt >= PLAYBACK_STALL_TIMEOUT) {
        this._post("pause");
        this._markFailed();
      }
    }, PLAYBACK_STALL_TIMEOUT);
  }

  _markFailed() {
    window.clearTimeout(this._playbackStartTimeout);
    window.clearTimeout(this._stallTimeout);
    window.clearTimeout(this._frameRevealTimeout);
    this.removeAttribute("has-frame");
    this.setAttribute("failed", "");
    this._exposeProvider(false);
    this._setPlaying(false);
  }

  _exposeProvider(expose) {
    this.toggleAttribute("provider-controls-exposed", expose);
    if (this._playerContainer) {
      this._playerContainer.style.zIndex = expose ? "2" : "";
    }
  }

  _setPlaying(playing) {
    this.toggleAttribute("playing", playing);
  }
}

if (!window.customElements.get("responsive-video-embed")) {
  window.customElements.define("responsive-video-embed", ResponsiveVideoEmbed);
}
